import React, { useEffect, useState, useMemo } from 'react'
import {
  Box,
  TextField,
  Checkbox,
  MenuItem,
  ListItemText,
  Popover,
  Radio,
  InputAdornment,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { MUIButton } from '../../Shared'
import styles from './ListingFilter.module.scss'
import MUIDateTimePicker from '../../MUIDateTimePicker'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { CrossFilterIcon } from '../../Icons/Edit'
import { SearchIcon, CheckboxIcon, CheckboxCheckedIcon } from '../../Icons'
import { CustomTooltip } from '../../Shared/CustomToolTip/CustomToolTip'
import RadioCheckedIcon from '../../Icons/RadioCheckedIcon'
import RadioIcon from '../../Icons/RadioIcon'

interface ListingFilterProps {
  lable: string
  id: string
  searchable: boolean
  clearAll: boolean
  clearFilter?: boolean
  options: { key: string; value: string }[]
  searchFunction?: (filter: any, id: string) => void
  isFilterSingleSelect?: boolean
  filters?: any
  clearFilterTrigger?: number
  type?: 'date'
}

const EndIcon = ({ open }: { open: boolean }) => {
  return open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
}

const ListingFilter: React.FC<ListingFilterProps> = ({
  searchable,
  lable,
  id,
  clearFilter,
  options,
  searchFunction,
  isFilterSingleSelect,
  clearFilterTrigger,
  filters,
  type,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null)
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [submittedOptions, setSubmittedOptions] = useState<string[]>([]) // Track submitted selections
  const [searchTerm, setSearchTerm] = useState('')
  const [clearLocationSearch, setClearLocationSearch] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [searchParams] = useSearchParams()

  const open = Boolean(anchorEl)
  const location = useLocation()
  const navigate = useNavigate()
  const displayOptions = useMemo(() => {
    if (!options || options.length === 0) return []
    const filteredOptions = options.filter(
      (option) => option?.value?.toLowerCase().includes(searchTerm?.toLowerCase()),
    )
    const submittedItems = filteredOptions.filter((option) => submittedOptions.includes(option.key))
    const remainingItems = filteredOptions.filter(
      (option) => !submittedOptions.includes(option.key),
    )

    return [
      ...submittedItems,
      ...remainingItems.filter((option) => !submittedOptions.includes(option.key)),
    ]
  }, [options, selectedOptions, submittedOptions, searchTerm])

  useEffect(() => {
    const stateParam = new URLSearchParams(location?.search)?.get('state')
    if (stateParam && options?.some((option) => option?.key === stateParam)) {
      setSelectedOptions([stateParam])
      setSubmittedOptions([stateParam]) // Mark as submitted since it comes from URL
    }
  }, [location?.search, options])

  useEffect(() => {
    if (clearFilter) {
      handleReset()
    }
  }, [clearFilter])

  useEffect(() => {
    if (clearLocationSearch) {
      if (id === 'state') {
        const searchParams = new URLSearchParams(location?.search)
        searchParams?.delete('state')
        navigate(
          {
            pathname: location?.pathname,
            search: searchParams?.toString(),
          },
          { replace: true },
        )
      }
      setClearLocationSearch(false)
    }
  }, [clearLocationSearch])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    const stateParam = searchParams.get('state')
    if (id === 'state' && (!filters || filters.length === 0) && stateParam) {
      setSelectedOptions([stateParam])
    } else {
      setSelectedOptions(submittedOptions)
    }
    setSearchTerm('')
  }

  const handleSelectOption = (key: any) => {
    let updatedSelection = [...selectedOptions]
    if (updatedSelection.includes(key)) {
      updatedSelection = updatedSelection?.filter((option) => option !== key)
    } else {
      updatedSelection = [...updatedSelection, key]
    }
    setSelectedOptions(updatedSelection)
  }

  const handleSingleSelect = (key: any) => {
    setSelectedOptions([key])
  }

  const handleReset = () => {
    if (type === 'date') {
      setSelectedDate(null)
      if (searchFunction) searchFunction({}, id)
    }
    if (selectedOptions.length > 0 || (filters && filters.length > 0)) {
      if (searchFunction) searchFunction({}, id)
    } else {
      if (filters?.length > 0 && searchFunction) {
        searchFunction({}, id)
      }
      setSelectedOptions([])
      setSearchTerm('')
      setClearLocationSearch(true)
    }
    setSelectedOptions([])
    setSubmittedOptions([])
    setSearchTerm('')
    setClearLocationSearch(true)
  }

  useEffect(() => {
    if (clearFilterTrigger) {
      setSelectedOptions([])
      setSubmittedOptions([])
      setSearchTerm('')
      setSelectedDate(null)
    }
  }, [clearFilterTrigger])

  const handleSave = () => {
    const filter: any = {}
    if (type === 'date' && selectedDate) {
      filter.filterBy = id
      filter.filterCondition = 'equals'
      filter.filterValue = selectedDate
    } else if (selectedOptions.length > 0) {
      filter.filterBy = id
      filter.filterCondition = 'in'
      filter.filterValue = selectedOptions
    }
    setSubmittedOptions([...selectedOptions])

    if (searchFunction) searchFunction(filter, id)
    setSearchTerm('')
    setAnchorEl(null)
    setClearLocationSearch(true)
  }

  useEffect(() => {
    if (filters?.length > 0) {
      setSelectedOptions(filters)
      setSubmittedOptions(filters)
    }
  }, [filters])

  return (
    <>
      <MUIButton
        className={`listing-filter-btn ${selectedOptions?.length && 'active'}`}
        type='button'
        label={lable}
        endIcon={
          selectedOptions?.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div className={`${styles['selected-count']}`}>{selectedOptions?.length}</div>
              <div
                onClick={(e: any) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleReset()
                }}
                className={`${styles['cross-filter-icon']}`}
              >
                <CrossFilterIcon />
              </div>
            </Box>
          ) : (
            <EndIcon open={open} />
          )
        }
        handleClickCB={handleClick}
        sx={{
          borderRadius: '8px',
          opacity: '0.7',
          backgroundColor: '#ffffff',
          border:
            (type === 'date' && selectedDate) || selectedOptions?.length > 0
              ? '2px solid #402080'
              : '',
          minWidth: 'max-content',
          '&:hover': {
            border:
              (type === 'date' && selectedDate) || selectedOptions?.length > 0
                ? '2px solid #402080'
                : '',
          },
        }}
        nonDisableProp
      />
      <Popover
        data-testid='popover_dialog'
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{ marginTop: '15px', borderRadius: '15px' }}
        className='filter-option-container'
      >
        <div className={styles['filter-option']}>
          <h5>{lable}</h5>
          {type === 'date' ? (
            <MUIDateTimePicker
              label={lable}
              type='date'
              value={selectedDate}
              onChange={setSelectedDate}
              name={id}
              error={false}
              disableFuture={true}
            />
          ) : searchable ? (
            <TextField
              placeholder={`Search ${lable}`}
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              className='listing-filter-search'
            />
          ) : (
            <Box
              sx={{
                alignContent: 'center',
                fontWeight: '600',
                fontSize: '20px',
                paddingLeft: '24px',
                paddingTop: '12px',
              }}
            >
              {lable}
            </Box>
          )}

          <div className={`${styles['filter-options-list']}`}>
            {displayOptions.map((item) => (
              <MenuItem
                key={item.key}
                value={item.key}
                onClick={() => {
                  isFilterSingleSelect ? handleSingleSelect(item.key) : handleSelectOption(item.key)
                }}
                className={`${styles['filter-option-item']}`}
              >
                {isFilterSingleSelect ? (
                  <Radio
                    data-testid={`filter_checkbox_${item.key}`}
                    checked={selectedOptions[0] === item.key}
                    className={styles['filter-checkbox']}
                    checkedIcon={<RadioCheckedIcon />}
                    icon={<RadioIcon />}
                  />
                ) : (
                  <Checkbox
                    icon={<CheckboxIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                    className={styles['filter-checkbox']}
                    data-testid={`filter_checkbox_${item.key}`}
                    checked={selectedOptions.includes(item.key)}
                  />
                )}
                <CustomTooltip
                  title={item.value}
                  placement='left'
                  arrow
                  disableInteractive
                  slotProps={{
                    tooltip: {
                      sx: {
                        position: 'relative',
                        right: '2.6rem',
                        mr: '0.5rem',
                        color: 'white',
                        backgroundColor: '#402080',
                        height: '32px',
                        fontSize: '14px',
                        '& .MuiTooltip-arrow': {
                          color: '#402080',
                        },
                      },
                    },
                  }}
                >
                  <ListItemText primary={item.value} className={`${styles['filter-text']}`} />
                </CustomTooltip>
              </MenuItem>
            ))}
          </div>
          <div className={styles['filter-popover-footer']}>
            <MUIButton
              width={50}
              fontSize={14}
              height='36px'
              type='button'
              label={'Apply'}
              variant='contained'
              handleClickCB={handleSave}
              nonDisableProp
            />
            <MUIButton
              width={50}
              fontSize={14}
              className={styles['reset-filters-btn']}
              height='36px'
              type='button'
              label={'Reset'}
              variant='text'
              handleClickCB={handleReset}
            />
          </div>
        </div>
      </Popover>
    </>
  )
}

export default ListingFilter
